import { type AxiosError, type AxiosInstance, type AxiosPromise, type AxiosResponse } from 'axios'
import { useMemo, useRef } from 'react'
import useSWR, { mutate as swrMutate, preload as swrPreload, type Cache, type MutatorCallback, type MutatorOptions, type Arguments as SWRArguments, type SWRConfiguration } from 'swr'
import { axiosClientRegistry } from '../http/axios/axiosClientRegistry'
import { type ClientNames } from '../http/types'
import { serializeKey, type Key } from './serializeKey'

type SafeParser<Data> = (data: unknown) => Data
type ConvertData<Source, Target> = (source: Source) => Target
type CustomFetcher<Data, Params> = (url: string, params?: Params) => AxiosPromise<Data>
type UseRequestOptions<Data, Error> = SWRConfiguration<AxiosResponse<Data>, AxiosError<Error>> & {
  /**
   * If you ever want to disable a query from automatically running, you can use the disabled = true option.
   * 1. all revalidate will be disabled
   */
  disabled?: boolean
}

const toPath = (url: string) => url.split('?')[0] ?? url
const composeFetcherWithSafeParser = <Data, Params>(
  fetcher: CustomFetcher<Data, Params>,
  safeParser?: SafeParser<Data>,
) => (url: string, params?: Params) => fetcher(url, params).then((response) => {
  const responseData: AxiosResponse<Data> = {
    ...response,
    data: safeParser ? safeParser(response.data) : response.data,
  }
  return responseData
})

export interface CreateRequestHelperConfig<URL extends SWRArguments, Data, Params, ConvertDataTarget> {
  /**
   * client 是用來定義這個 api 會使用哪一個 client 來發送 request
   */
  client: ClientNames
  /**
   * key 也就是 params serialize to key 的 function 是用來定義這個 api 所需要的 Params 並回傳這組 key 給 swr 使用
   */
  key: Key<Params, URL>
  /**
   * Set true to disable revalidation.
   */
  isImmutable?: boolean
  /**
   * mutateAllMatcher 是用來定義哪些 key 會被 mutateAll 觸發
   */
  mutateAllMatcher?: (key: string) => boolean
  /**
   * Set true to use mock client
   */
  mock?: boolean
  /**
   * Mock data
   */
  mockData?: Data
  /**
   * Custom fetcher
   */
  fetcher?: (client: AxiosInstance) => CustomFetcher<Data, Params>
  /**
   * 自定義 cache 的 provider，可以將資料存到 localStorage 中，以減少 API 呼叫次數
   * WARNING: provider 不可和 isImmutable 同時使用，否則資料存在 localStorage 之後就不會再重新取得
   */
  provider?: () => Cache
  /**
   * 可以透過 zod 自定義 schema 驗證 response 資料是否正確
   */
  safeParser?: SafeParser<Data>
  /**
   * 自定義 response 資料轉換成想要的格式
   */
  convertData?: ConvertData<Data, ConvertDataTarget>
}

export const createRequestHelper = <
  URL extends SWRArguments,
  Data,
  Params = undefined,
  Error = unknown,
  ConvertDataTarget = unknown,
>(config: CreateRequestHelperConfig<URL, Data, Params, ConvertDataTarget>) => {
  const {
    key: _key,
    isImmutable,
    mutateAllMatcher,
    client = 'fortune',
    mock,
    mockData,
    fetcher,
    provider,
    safeParser,
    convertData,
  } = config

  const getSwrFetcher = () => {
    const clientInstance = axiosClientRegistry.getClient(client, mock)
    if (typeof mockData !== 'undefined') {
      const axiosResponse: AxiosResponse<Data> = { data: mockData, status: 200, statusText: 'OK', headers: {}, config: {} as unknown as AxiosResponse<Data>['config'] }
      return (_: string) => new Promise<AxiosResponse<Data>>(resolve => setTimeout(() => resolve(axiosResponse), 500))
    }
    return fetcher
      ? composeFetcherWithSafeParser((url: string, params?: Params) => fetcher(clientInstance)(toPath(url), params), safeParser)
      : composeFetcherWithSafeParser((url: string) => clientInstance.get<Data>(url), safeParser)
  }
  const customCache = provider ? provider() : undefined

  const useRequest = (
    params?: Params,
    options: UseRequestOptions<Data, Error> = {},
  ) => {
    const { disabled, ...restOptions } = options
    const nextKey = useMemo(() => serializeKey(_key, params), [params])
    const tempKeyStore = useRef<typeof nextKey>(undefined)
    const key = disabled ? tempKeyStore.current : nextKey
    tempKeyStore.current = key

    if (isImmutable) {
      restOptions.revalidateOnFocus = false
      restOptions.revalidateIfStale = false
      restOptions.revalidateOnReconnect = false
    }
    if (disabled) {
      restOptions.revalidateOnFocus = false
      restOptions.revalidateIfStale = false
      restOptions.revalidateOnReconnect = false
      restOptions.revalidateOnMount = false
    }
    const hasCustomCache = customCache && typeof key === 'string'

    const fallbackData: AxiosResponse<Data> | undefined = useMemo(() => {
      if (!hasCustomCache) return undefined
      const cacheData = customCache.get(key)?.data
      if (!cacheData) return undefined
      return {
        ...cacheData,
        data: safeParser ? safeParser(cacheData.data) : cacheData.data,
      }
    }, [hasCustomCache, key])

    const response = useSWR<AxiosResponse<Data>, AxiosError<Error>>(key, (url: string) => {
      const swrFetcher = getSwrFetcher()
      return swrFetcher(url, params)
    }, {
      fallbackData,
      ...restOptions,
    })

    if (hasCustomCache) {
      customCache.set(key, {
        data: response.data,
        error: response.error,
        isValidating: response.isValidating,
        isLoading: response.isLoading,
      })
    }

    const convertedData = useMemo(() => convertData && response.data ? convertData(response.data.data) : undefined, [response.data])

    return {
      ...response,
      convertedData,
    }
  }

  const preload = (params?: Params) => swrPreload(serializeKey(_key, params), (url: string) => {
    const swrFetcher = getSwrFetcher()
    return swrFetcher(url, params)
  })

  const mutate = (
    params?: Params,
    data?: { data: Data } | Promise<{ data: Data }> | MutatorCallback<{ data: Data }>,
    opts?: boolean | MutatorOptions<{ data: Data }>,
  ) => swrMutate(serializeKey(_key, params), data, opts)

  const mutateAll = mutateAllMatcher ? (data?: { data: Data } | Promise<{ data: Data }> | MutatorCallback<{ data: Data }>, opts?: boolean | MutatorOptions<{ data: Data }>) => swrMutate(key => typeof key === 'string' && mutateAllMatcher(key), data, opts) : undefined

  return {
    /**
     * 回傳給應用層使用的 hook，目前僅接受兩個參數，params, swrOptions，未來可能會多一個 axios options 目前使用上還沒碰到如果有需要再加
     */
    useRequest,
    /**
     * preload 一樣是 swr 內建的機制只是同樣用 params serialize to key 的方式在使用上更簡單，常用在需要增加使用者體驗時預先 fetch api，
     * 例如在列表如果滑鼠有 hover 到某個 item 時就可以預先撈取裡面的資料。
     */
    preload,
    /**
     * 使用 params serialize to key 來 revalidate 的語法糖，可以跨組件使用，常用於 update 後重新取值，要注意的是 params 必須和你目前呼叫的 API 完全相同才會有效
     */
    mutate,
    /**
     * mutateAll 透過 matcher 的判斷篩選所需要重新 validate 的 api hook，根據官方的設計只有 mounted 的 hook 會 revalidate，
     * 常用在重新撈取帶有複雜 query-string 的 list api，因為是用 matcher 去判斷 key 的條件所以可能會有多隻 api 觸發 revalidate，
     * 如果可以的話還是建議先使用一般的 mutate。
     */
    mutateAll,
    /**
     * 取得此 api hook 的 fetcher，可以當成一般的 fetch api 使用
     */
    getSwrFetcher,
  }
}
